import React from 'react'
import toast, { Toaster as HotToaster, ToastBar, type ToastOptions } from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'

// Portal-specific toast themes
const getPortalToastTheme = (userRole: string | null, isSuperAdmin: boolean, isTrainerMode: boolean) => {
  if (isSuperAdmin || userRole === 'admin') {
    return {
      primary: '#FFD700',
      primaryHover: '#FFC700',
      success: '#22c55e',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
      bg: '#1a1a1a',
      text: '#ffffff',
      border: 'rgba(255, 215, 0, 0.2)'
    }
  }
  
  if (isTrainerMode) {
    return {
      primary: '#10B981',
      primaryHover: '#059669',
      success: '#22c55e',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
      bg: '#1a1a1a',
      text: '#ffffff',
      border: 'rgba(16, 185, 129, 0.2)'
    }
  }
  
  if (userRole === 'staff') {
    return {
      primary: '#3B82F6',
      primaryHover: '#2563EB',
      success: '#22c55e',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
      bg: '#1a1a1a',
      text: '#ffffff',
      border: 'rgba(59, 130, 246, 0.2)'
    }
  }
  
  if (userRole === 'member') {
    return {
      primary: '#C0C0C0',
      primaryHover: '#A0A0A0',
      success: '#22c55e',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
      bg: '#1a1a1a',
      text: '#ffffff',
      border: 'rgba(192, 192, 192, 0.2)'
    }
  }
  
  // Default theme
  return {
    primary: '#FFD700',
    primaryHover: '#FFC700',
    success: '#22c55e',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6',
    bg: '#1a1a1a',
    text: '#ffffff',
    border: 'rgba(255, 255, 255, 0.1)'
  }
}

export const PortalToaster = () => {
  const { profile, isSuperAdmin, isTrainerMode } = useAuth()
  const theme = getPortalToastTheme(profile?.role || null, isSuperAdmin, isTrainerMode)
  
  return (
    <HotToaster
      position="top-right"
      reverseOrder={false}
      gutter={8}
      containerStyle={{
        top: 20,
        right: 20,
      }}
      toastOptions={{
        duration: 4000,
        style: {
          background: theme.bg,
          color: theme.text,
          border: `1px solid ${theme.border}`,
          borderRadius: '8px',
          fontSize: '14px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          padding: '12px 16px',
          maxWidth: '400px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
        },
        success: {
          iconTheme: {
            primary: theme.success,
            secondary: theme.bg,
          },
          style: {
            border: `1px solid rgba(34, 197, 94, 0.3)`,
          },
        },
        error: {
          iconTheme: {
            primary: theme.error,
            secondary: theme.bg,
          },
          style: {
            border: `1px solid rgba(239, 68, 68, 0.3)`,
          },
        },
        loading: {
          iconTheme: {
            primary: theme.primary,
            secondary: theme.bg,
          },
        },
      }}
    >
      {(t) => (
        <ToastBar toast={t}>
          {({ icon, message }) => (
            <>
              {icon}
              <div style={{ flex: 1, marginLeft: '8px' }}>{message}</div>
              {t.type !== 'loading' && (
                <button
                  onClick={() => toast.dismiss(t.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.5)',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '0 4px',
                    marginLeft: '8px',
                    transition: 'color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                  }}
                >
                  ×
                </button>
              )}
            </>
          )}
        </ToastBar>
      )}
    </HotToaster>
  )
}

// Enhanced toast functions with portal styling
export const portalToast = {
  success: (message: string, options?: ToastOptions) => {
    return toast.success(message, {
      ...options,
      className: 'portal-toast portal-toast-success',
    })
  },

  error: (message: string, options?: ToastOptions) => {
    return toast.error(message, {
      ...options,
      className: 'portal-toast portal-toast-error',
    })
  },

  loading: (message: string, options?: ToastOptions) => {
    return toast.loading(message, {
      ...options,
      className: 'portal-toast portal-toast-loading',
    })
  },

  promise: <T,>(
    promise: Promise<T>,
    msgs: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((err: Error) => string)
    },
    options?: ToastOptions
  ) => {
    return toast.promise(promise, msgs, {
      ...options,
      className: 'portal-toast',
    })
  },

  custom: (message: React.ReactNode, options?: ToastOptions) => {
    return toast.custom(message, {
      ...options,
      className: 'portal-toast portal-toast-custom',
    })
  },
  
  dismiss: toast.dismiss,
  remove: toast.remove,
}

// Notification-style toast (similar to NotificationToast)
export const notificationToast = {
  show: (options: {
    title: string
    message: string
    type?: 'success' | 'error' | 'warning' | 'info'
    action?: {
      label: string
      onClick: () => void
    }
    duration?: number
  }) => {
    const { title, message, type = 'info', action, duration = 4000 } = options
    
    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️',
    }
    
    return toast.custom(
      (t) => (
        <div
          className={`
            portal-notification-toast
            ${t.visible ? 'animate-enter' : 'animate-leave'}
          `}
          onClick={() => action?.onClick()}
          style={{
            cursor: action ? 'pointer' : 'default',
          }}
        >
          <div className="flex items-start gap-3">
            <span className="text-lg">{icons[type]}</span>
            <div className="flex-1">
              <div className="font-semibold mb-1">{title}</div>
              <div className="text-sm opacity-80">{message}</div>
              {action && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    action.onClick()
                    toast.dismiss(t.id)
                  }}
                  className="mt-2 text-xs font-medium px-2 py-1 rounded bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
                >
                  {action.label} →
                </button>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                toast.dismiss(t.id)
              }}
              className="text-lg opacity-50 hover:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
        </div>
      ),
      { duration }
    )
  },
}

// Export a simple showNotification function for use in other files
export const showNotification = notificationToast.show